import { useEffect, useState } from "react";
import { api, formatFCFA } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import {
  Users, Wallet, ArrowLeftRight, Briefcase, IdCard, FileText,
  TrendingUp, TrendingDown, AlertTriangle, Activity,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, PieChart, Pie, Cell, Legend,
} from "recharts";

const KPI_CARDS = [
  { key: "players", label: "Joueuses inscrites", icon: Users, accent: "emerald" },
  { key: "balance", label: "Solde du club", icon: Wallet, accent: "emerald" },
  { key: "transfers", label: "Transferts en cours", icon: ArrowLeftRight, accent: "amber" },
  { key: "employees", label: "Personnel", icon: Briefcase, accent: "slate" },
  { key: "members", label: "Membres", icon: IdCard, accent: "slate" },
  { key: "documents", label: "Documents générés", icon: FileText, accent: "slate" },
];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [chart, setChart] = useState([]);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
    api.get("/dashboard/finance-chart").then((r) => setChart(r.data));
  }, []);

  if (!stats) {
    return (
      <div>
        <PageHeader title="Tableau de bord" subtitle="Vue d'ensemble du club AMAL VBC" />
        <div className="p-8 text-slate-400">Chargement...</div>
      </div>
    );
  }

  const kpis = {
    players: { value: stats.players.total, sub: `${stats.players.PRO} PRO • ${stats.players.AMATEUR} AMA • ${stats.players.U17} U17 • ${stats.players.U13} U13` },
    balance: { value: formatFCFA(stats.finance.balance), sub: `Entrées : ${formatFCFA(stats.finance.income)}` },
    transfers: { value: stats.transfers.pending, sub: `Total historique : ${stats.transfers.total}` },
    employees: { value: stats.employees, sub: "Salariés, bénévoles, staff" },
    members: { value: stats.members, sub: "Adhérents et supporters" },
    documents: { value: stats.documents, sub: "Documents officiels générés" },
  };

  const categoryData = [
    { name: "PRO", value: stats.players.PRO, color: "#00A859" },
    { name: "Amateur", value: stats.players.AMATEUR, color: "#10B981" },
    { name: "U17", value: stats.players.U17, color: "#34D399" },
    { name: "U13", value: stats.players.U13, color: "#6EE7B7" },
  ];

  return (
    <div data-testid="dashboard-page">
      <PageHeader title="Tableau de bord" subtitle="Vue d'ensemble du club AMAL VBC — Saison 2025-2026" />

      <div className="p-6 lg:p-8 space-y-6">
        {/* KPI cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          {KPI_CARDS.map((card) => {
            const Icon = card.icon;
            const data = kpis[card.key];
            return (
              <div
                key={card.key}
                data-testid={`kpi-${card.key}`}
                className="stat-card bg-white border border-slate-200 rounded-md p-5"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
                    {card.label}
                  </div>
                  <Icon className="h-4 w-4 text-emerald-600" />
                </div>
                <div className="font-barlow font-bold text-3xl tracking-tight text-slate-900 leading-none">
                  {data.value}
                </div>
                <div className="text-xs text-slate-500 mt-2 truncate">{data.sub}</div>
              </div>
            );
          })}
        </div>

        {/* Charts row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Finance chart */}
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-md p-6" data-testid="finance-chart-card">
            <div className="flex items-center justify-between mb-1">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Évolution financière</div>
                <h3 className="font-barlow font-bold text-xl tracking-tight text-slate-900">Entrées / Sorties mensuelles</h3>
              </div>
              <div className="text-right">
                <div className="text-xs text-slate-500">Solde global</div>
                <div className={`font-barlow font-bold text-2xl ${stats.finance.balance >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                  {formatFCFA(stats.finance.balance)}
                </div>
              </div>
            </div>
            <div className="h-64 mt-4">
              {chart.length === 0 ? (
                <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                  Aucune donnée financière disponible
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chart} margin={{ top: 10, right: 0, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748B" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                    <Tooltip
                      formatter={(v) => formatFCFA(v)}
                      contentStyle={{ border: "1px solid #E2E8F0", borderRadius: 4, fontSize: 12 }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="income" name="Entrées" fill="#00A859" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="expense" name="Sorties" fill="#F59E0B" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Players by category */}
          <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="category-chart-card">
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Répartition</div>
            <h3 className="font-barlow font-bold text-xl tracking-tight text-slate-900 mb-3">Joueuses par catégorie</h3>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={categoryData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={42}
                    outerRadius={70}
                    paddingAngle={2}
                  >
                    {categoryData.map((d) => (
                      <Cell key={d.name} fill={d.color} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ border: "1px solid #E2E8F0", borderRadius: 4, fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {categoryData.map((d) => (
                <div key={d.name} className="flex items-center gap-2 text-xs">
                  <span className="h-2.5 w-2.5 rounded-sm" style={{ background: d.color }} />
                  <span className="text-slate-600">{d.name}</span>
                  <span className="ml-auto font-semibold text-slate-900">{d.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Alerts + finance summary */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-md p-6" data-testid="alerts-card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Alertes administratives</div>
                <h3 className="font-barlow font-bold text-xl tracking-tight">Notifications</h3>
              </div>
              <AlertTriangle className="h-4 w-4 text-amber-500" />
            </div>
            {stats.alerts.length === 0 ? (
              <div className="text-sm text-slate-400 py-8 text-center">Aucune alerte. Tout est en ordre.</div>
            ) : (
              <ul className="space-y-2">
                {stats.alerts.map((a) => {
                  const colors = {
                    warning: "border-amber-200 bg-amber-50 text-amber-800",
                    danger: "border-red-200 bg-red-50 text-red-800",
                    info: "border-blue-200 bg-blue-50 text-blue-800",
                  };
                  return (
                    <li
                      key={`${a.type}-${a.message}`}
                      data-testid={`alert-${a.type}`}
                      className={`flex items-center gap-3 border rounded-md px-4 py-3 text-sm ${colors[a.level] || colors.info}`}
                    >
                      <span className="h-2 w-2 rounded-full bg-current" />
                      <span className="flex-1">{a.message}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="bg-white border border-slate-200 rounded-md p-6">
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Synthèse financière</div>
            <h3 className="font-barlow font-bold text-xl tracking-tight mb-4">Flux du club</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <TrendingUp className="h-4 w-4 text-emerald-600" /> Entrées
                </div>
                <div className="font-semibold text-emerald-700" data-testid="finance-income">
                  {formatFCFA(stats.finance.income)}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <TrendingDown className="h-4 w-4 text-amber-600" /> Sorties
                </div>
                <div className="font-semibold text-amber-700" data-testid="finance-expense">
                  {formatFCFA(stats.finance.expense)}
                </div>
              </div>
              <div className="border-t border-slate-200 pt-3 flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <Activity className="h-4 w-4" /> Solde
                </div>
                <div className={`font-barlow font-bold text-xl ${stats.finance.balance >= 0 ? "text-emerald-700" : "text-red-700"}`} data-testid="finance-balance">
                  {formatFCFA(stats.finance.balance)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
